/* BG lander buy box (unit 50c) — <bg-lp-c-buy-box> in sections/bg-lp-c-buy-box.liquid.
   Port of the live GemPages "sc-v2" script:
   - size cards are radios (click / Enter / Space); prices and per-serve lines follow the size and, once step 2 is
     open, the chosen purchase type;
   - "Next Step →" opens step 2 (one-time preselected, subscription copy collapsed on phones) and glides the page so
     the payment icons sit at the bottom of the screen (1.35s ease-in-out, instant with reduced motion);
   - purchase cards: subscription expands its copy; the page re-anchors to the payment icons;
   - the guarantee card also opens step 2; tapping the payment icons submits the form;
   - the form posts natively to /cart/add: one-time -> return_to /checkout, subscription -> selling_plan + /cart;
   - Meta pixel AddToCart on Buy Now when fbq exists (live parity);
   - data-device="ios" on iPhone/iPad (live sizes its type slightly smaller there). */
if (!customElements.get('bg-lp-c-buy-box')) {
  customElements.define(
    'bg-lp-c-buy-box',
    class BgLpCBuyBox extends HTMLElement {
      connectedCallback() {
        const ua = navigator.userAgent || '';
        if (/iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
          this.dataset.device = 'ios';
        }

        this.sizes = Array.from(this.querySelectorAll('[data-size]'));
        this.purchases = Array.from(this.querySelectorAll('[data-purchase]'));
        this.form = this.querySelector('[data-form]');
        this.submit = this.querySelector('[data-submit]');
        this.next = this.querySelector('[data-next]');
        this.purchasesGroup = this.querySelector('[data-purchases]');
        this.payIcons = this.querySelector('.bg-lp-c-buy__pay-icons');
        this.selectedSize = this.sizes.find((size) => size.classList.contains('is-selected')) || this.sizes[0] || null;
        this.selectedPurchase = 'single';

        this.setStepHidden(true);

        const activate = (element, handler) => {
          element.addEventListener('click', handler);
          element.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handler(event);
            }
          });
        };

        this.sizes.forEach((size) =>
          activate(size, () => {
            this.selectedSize = size;
            this.update();
          })
        );

        this.next?.addEventListener('click', () => this.proceed());

        this.purchases.forEach((option) =>
          activate(option, () => {
            this.selectedPurchase = option.dataset.purchase === 'subscribe' ? 'subscribe' : 'single';
            if (this.selectedPurchase === 'subscribe') this.dataset.subCopyExpanded = 'true';
            this.update();
            this.anchorPaymentIcons();
          })
        );

        this.querySelectorAll('[data-reveal-trigger]').forEach((trigger) =>
          activate(trigger, () => {
            this.reveal();
            this.update();
          })
        );

        this.querySelectorAll('[data-cart-trigger]').forEach((trigger) =>
          activate(trigger, (event) => {
            event.preventDefault();
            this.submit?.click();
          })
        );

        this.form?.addEventListener('submit', (event) => {
          if (this.submit?.classList.contains('is-loading')) {
            event.preventDefault();
            return;
          }
          this.setLoading(true);
        });
        window.addEventListener('pageshow', () => this.setLoading(false));

        this.submit?.addEventListener('click', () => this.trackAddToCart());

        this.update();
      }

      /* API used by bg-lp-c-lander.js (hero "Shop Now" / "or Learn More") */
      isRevealed() {
        return this.dataset.purchaseRevealed === 'true';
      }

      nextTarget() {
        return this.next && this.next.getClientRects().length ? this.next : this;
      }

      sizeTarget() {
        return this.querySelector('[data-sizes] .bg-lp-c-buy__legend') || this;
      }

      setStepHidden(hidden) {
        this.querySelectorAll('[data-step]').forEach((element) => {
          if (hidden) {
            element.setAttribute('aria-hidden', 'true');
          } else {
            element.removeAttribute('aria-hidden');
          }
        });
      }

      reveal() {
        if (this.isRevealed()) return;
        this.dataset.purchaseRevealed = 'true';
        this.setStepHidden(false);
        // the connector stays decorative
        this.querySelector('.bg-lp-c-buy__connector')?.setAttribute('aria-hidden', 'true');
      }

      proceed() {
        this.selectedSize = this.selectedSize || this.sizes[0] || null;
        this.selectedPurchase = 'single';
        this.dataset.subCopyExpanded = 'false';
        this.reveal();
        this.update();
        window.setTimeout(() => {
          const top = this.paymentScrollTop();
          if (top !== null) {
            this.glideTo(top, 1350);
            window.setTimeout(() => {
              const corrected = this.paymentScrollTop();
              if (corrected !== null && Math.abs(corrected - window.scrollY) > 3) this.glideTo(corrected, 260);
            }, 1470);
          }
          window.setTimeout(() => this.purchasesGroup?.focus({ preventScroll: true }), 1530);
        }, 90);
      }

      anchorPaymentIcons() {
        if (!this.isRevealed()) return;
        window.setTimeout(() => {
          const top = this.paymentScrollTop();
          if (top === null) return;
          this.glideTo(top, 420);
          window.setTimeout(() => {
            const corrected = this.paymentScrollTop();
            if (corrected !== null && Math.abs(corrected - window.scrollY) > 3) this.glideTo(corrected, 220);
          }, 510);
        }, 70);
      }

      paymentScrollTop() {
        if (!this.payIcons || !this.payIcons.getClientRects().length) return null;
        const viewport = (window.visualViewport && window.visualViewport.height) || window.innerHeight;
        const offset = (window.visualViewport && window.visualViewport.offsetTop) || 0;
        const cushion = Math.max(8, Math.min(16, Math.round(viewport * 0.014)));
        return this.payIcons.getBoundingClientRect().bottom + window.scrollY - viewport - offset + cushion;
      }

      glideTo(top, duration) {
        const destination = Math.max(0, top);
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          window.scrollTo(0, destination);
          return;
        }
        const start = window.scrollY;
        const distance = destination - start;
        let started = null;
        const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
        const step = (time) => {
          if (started === null) started = time;
          const progress = Math.min(1, (time - started) / duration);
          window.scrollTo(0, start + distance * ease(progress));
          if (progress < 1) window.requestAnimationFrame(step);
        };
        window.requestAnimationFrame(step);
      }

      money(value) {
        return `$${Number(value || 0).toFixed(2)}`;
      }

      exactMoney(value) {
        const n = Math.round(Number(value || 0) * 100) / 100;
        return `$${Math.abs(n - Math.round(n)) < 0.005 ? String(Math.round(n)) : n.toFixed(2)}`;
      }

      perServe(size, purchase) {
        return purchase === 'subscribe' ? size.dataset.subPerserve : size.dataset.oncePerserve;
      }

      total(size, purchase) {
        return purchase === 'subscribe' ? size.dataset.subTotal : size.dataset.onceTotal;
      }

      setSelected(options, selected) {
        options.forEach((option) => {
          const active = option === selected;
          option.classList.toggle('is-selected', active);
          option.setAttribute('aria-checked', active ? 'true' : 'false');
        });
      }

      update() {
        const size = this.selectedSize;
        this.setSelected(this.sizes, size);
        this.setSelected(
          this.purchases,
          this.purchases.find((option) => option.dataset.purchase === this.selectedPurchase)
        );
        if (!size) return;

        const revealed = this.isRevealed();
        const sizeMode = revealed ? this.selectedPurchase : 'single';
        const suffix = (element) => element.textContent.replace(/^\s*\$[\d.,]+\s*/, '');

        this.sizes.forEach((option) => {
          const perServe = option.querySelector('[data-size-perserve]');
          const total = option.querySelector('[data-size-total]');
          if (perServe) perServe.textContent = `${this.money(this.perServe(option, sizeMode))} ${suffix(perServe)}`;
          if (total) {
            total.textContent = revealed
              ? this.exactMoney(this.total(option, this.selectedPurchase))
              : this.exactMoney(option.dataset.price);
          }
        });

        const subPerServe = this.querySelector('[data-purchase-perserve="subscribe"]');
        const oncePerServe = this.querySelector('[data-purchase-perserve="single"]');
        if (subPerServe) subPerServe.textContent = `${this.money(size.dataset.subPerserve)} ${suffix(subPerServe)}`;
        if (oncePerServe) oncePerServe.textContent = `${this.money(size.dataset.oncePerserve)} ${suffix(oncePerServe)}`;

        const once = Number(size.dataset.onceTotal);
        const sub = Number(size.dataset.subTotal);
        this.querySelectorAll('[data-sub-total-out]').forEach((el) => (el.textContent = this.exactMoney(sub)));
        this.querySelectorAll('[data-single-total]').forEach((el) => (el.textContent = this.exactMoney(once)));
        this.querySelectorAll('[data-sub-compare]').forEach((el) => (el.textContent = this.exactMoney(once)));
        this.querySelectorAll('[data-sub-savings]').forEach((el) => (el.textContent = this.exactMoney(Math.max(0, once - sub))));

        const name = (size.querySelector('.bg-lp-c-buy__name')?.textContent || '').replace(/\s+/g, ' ').trim().replace(/^The\s+/i, '');
        const volume = (size.dataset.volumeLabel || '').replace(/\s+/g, ' ').trim().split(' ');
        this.querySelectorAll('[data-selected-name]').forEach((el) => (el.textContent = name));
        this.querySelectorAll('[data-selected-ml-number]').forEach((el) => (el.textContent = volume[0] || ''));
        this.querySelectorAll('[data-selected-ml-unit]').forEach((el) => (el.textContent = volume.slice(1).join(' ')));

        this.syncForm();
      }

      syncForm() {
        if (!this.form || !this.selectedSize) return;
        const subscribe = this.selectedPurchase === 'subscribe';
        const vid = this.form.querySelector('[data-vid]');
        const plan = this.form.querySelector('[data-plan-input]');
        const type = this.form.querySelector('[data-purchase-attr]');
        const returnTo = this.form.querySelector('[data-return-to]');
        if (vid) vid.value = this.selectedSize.dataset.variant || '';
        if (type) type.value = subscribe ? 'subscribe' : 'onetime';
        if (returnTo) returnTo.value = subscribe ? this.dataset.returnSub || '/cart' : this.dataset.returnOnce || '/checkout';
        if (plan) {
          plan.disabled = !subscribe;
          plan.value = subscribe ? this.selectedSize.dataset.plan || '' : '';
        }
      }

      setLoading(loading) {
        if (!this.submit) return;
        this.submit.classList.toggle('is-loading', loading);
        this.submit.setAttribute('aria-busy', loading ? 'true' : 'false');
      }

      trackAddToCart() {
        try {
          if (typeof window.fbq !== 'function' || !this.selectedSize) return;
          const price = parseFloat(this.selectedSize.dataset.price || '0');
          const value = this.selectedPurchase === 'subscribe' ? price * 0.85 : price;
          window.fbq('track', 'AddToCart', {
            content_ids: [this.selectedSize.dataset.variant || ''],
            content_type: 'product',
            value: Math.round(value * 100) / 100,
            currency: this.dataset.currency || 'NZD',
            num_items: 1,
          });
        } catch (e) {
          /* tracking must never block checkout */
        }
      }
    }
  );
}
